<template>
  
  <ion-page id="verbalplay-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />

      </div>

      <div v-if="showState == SHOW_MULTIPLE_OPTIONS">
        <MultiOptions :displayArray="multipleOptionsArray"
                        :wordsSpoken="multiOptionsWordsSpoken"
                        :wordsSpokenTrigger="multiOptionsWordsSpokenTrigger"
                        @item-selected="multiOptionsClick"
                        @toolbar-click="multiOptionsToolbarClick" />
      </div>

      <div v-if="showState == SHOW_SINGLE_OPTION">
        <SingleOption :displayArray="singleOptionArray"
                        :descriptionHeading="singleOptionHeading"
                        :initialSelected="singleOptionSelected"
                        :wordsSpoken="singleOptionWordsSpoken"
                        :wordsSpokenTrigger="singleOptionWordsSpokenTrigger"

                        @item-selected="singleOptionClick" 
                        @toolbar-click="singleOptionToolbarClick"/>
      </div>

      <div v-show="showState == SHOW_PLAY" >
        <TopLine :style="styleTopline"  :title="playTitle" :microphoneIsMuted="microphoneIsMuted" 
                      @topline-click="toplineClick"/>
        <div :style="styleMiddle">

          <div id="the-board"  :style="styleBoard">
              <CMChessboard :fen="forceFen" :validMoves="availableMoves" 
                        :orientation="orientation" 
                        :boardSize="boardSize" 
                        :animateOnePly="animateFen"
                        :animateSequence="animateSequeneOfFens"
                        :pieceSet="pieceSet"
                        @board-move="boardMove" />
          </div>
          <div :style="styleTextArea" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
                <div v-html="textareaMessage"></div>
          </div>
        </div>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>

  

</template>

<script setup>

  import { IonPage, IonContent  } from '@ionic/vue';

  import CMChessboard from '@/components/CMChessboard.vue'
  import { Chess, validateFen } from 'chess.js'

  import { computed } from "@vue/reactivity"

  import { ref, watch , onMounted, onBeforeUnmount, onUnmounted } from 'vue'
  import ToolBar from '@/components/ToolBar.vue'
  import MultiOptions from '@/components/MultiOptions.vue'
  import SingleOption from '@/components/SingleOption.vue'

  import { useConnectionStore } from '@/stores/connectionStore';
  import { doStartupCheck } from '@/composables/startupCheck.js'

  import ExplainTour from '@/components/ExplainTour.vue'

  import { buildWordList, parseLichessCommands, parseComputerCommands, parsePlay, 
                    parseGameWordsSaid, processDescribe , processArrowKey }
                    from '@/composables/parsePlay.js'
  import { buildWordListFromChoiceList , matchValue, parseGeneralWordsSaid } from '@/composables/wordlistSupport.js'
  import { buildPieceLocationStrings, getPieceSetName , columnToNumber, 
        buildAvailableMoves, buildVerbalSan} from '@/composables/blindfoldSupport.js'

  import { buildStyleToolbar, buildStyleTopline, availableWidthHeight, buildStyleMiddle,
              calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'
  import { translateEnglishWords, translatePhrase } from '@/composables/translation';

  import { useWindowSizeStore } from '../../stores/windowSizeStore';
  import TopLine from '@/components/TopLine.vue'
  import { useGetData , generateErrorAlertText } from '../../composables/getData.js'

const emit = defineEmits(['where-next','word-list','mic-is-muted','string-to-speak'])

const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()

  // once we play, don't start on landing page
  connectionStore.setStartOnLandingPage("no")
  
  var playType = connectionStore.getPlayType
  const fileWords = connectionStore.getFileWords
  const languageInUse = connectionStore.getLanguageInUse

  const singleOptionWordsSpokenTrigger = ref(0)
  const multiOptionsWordsSpokenTrigger = ref(0)
  const explainTourWordsSpokenTrigger = ref(0)

  const textareaMessage = ref("")

  const availableMoves = ref([])
  var chess = new Chess()

  var openGameId = 0
  var moveNumber = 0
  var canTakeBack = false
  var playingAsWhite = 1
  var engineMoveJson = null
  var engineMoveToRepeat = ""
  var playKeywordRequired = "play"

  const playTitle = ref("Unknown")
  //console.log("PLAY TYPE",playType)
  switch (playType) {
    case "continue" :
      openGameId = connectionStore.getPlayComputerGameId
      playTitle.value = translatePhrase("Continue Game")
      break
    case "computer" :
      playTitle.value = translatePhrase("Play Computer")
      break
    case "blindfold" :
      playTitle.value = translatePhrase("Play Blindfold")
      break
  }

  const isDark = ref(connectionStore.getDarkmode != "light")
  
  const NO_GAME = 0
  const IN_GAME = 1
  const GAME_ENDED = 2

  var gameState = NO_GAME

  const SHOW_PLAY = 0
  const SHOW_MULTIPLE_OPTIONS = 1
  const SHOW_SINGLE_OPTION = 2
  const SHOW_EXPLAINTOUR = 3

  const showState = ref(SHOW_PLAY)
  var singleOptionForEndGame = false
  var explainForState = SHOW_PLAY
  var explainForStateWordList = ""

  const TOOLBAR_START_GAME = 0
  const TOOLBAR_TAKEBACK_OPTIONS = 1
  const TOOLBAR_REPLAY_REVIEW = 2
  const TOOLBAR_BLINDFOLD_REVEAL = 3
  const TOOLBAR_BLINDFOLD_EXPLAIN = 4
  const TOOLBAR_COMPUTER_EXPLAIN = 3

  const toolbarBlindElements = [
    { index : TOOLBAR_START_GAME, label : translateEnglishWords("Start Game") ,disabled : false},
    { index : TOOLBAR_TAKEBACK_OPTIONS, label : translateEnglishWords("Options") ,disabled : false},
    { index : TOOLBAR_REPLAY_REVIEW, label : translateEnglishWords("Review") ,disabled : true},
    { index : TOOLBAR_BLINDFOLD_REVEAL, label : translateEnglishWords("Reveal") ,disabled : true},
    { index : TOOLBAR_BLINDFOLD_EXPLAIN, label : translateEnglishWords("Explain") ,disabled : false},
  ]

   const toolbarComputerElements = [
    { index : TOOLBAR_START_GAME, label : translateEnglishWords("Start Game") ,disabled : false},
    { index : TOOLBAR_TAKEBACK_OPTIONS, label : translateEnglishWords("Options") ,disabled : false},
    { index : TOOLBAR_REPLAY_REVIEW, label : translateEnglishWords("Replay") ,disabled : true},
    { index : TOOLBAR_COMPUTER_EXPLAIN, label : translateEnglishWords("Explain") ,disabled : false},
  ]

  const toolbarElements = ref((playType == "blindfold") ? toolbarBlindElements : toolbarComputerElements)

  const toolbarTrigger = ref(0)
  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  const microphoneTrigger = ref(0)

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")

  const multiOptionsWordsSpoken = ref("")

  const singleOptionWordsSpoken = ref("")

  const wordlistPlay = buildWordList("computer",languageInUse)
  const wordlistExplain = "command done scroll up down"
  const wordlistTrigger = ref(0)
  const wordlist = ref(wordlistPlay)
  emit("word-list",wordlist.value)

  const explainTitle = ref("Title")
  const explainMessage = ref("Message")
    
  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const TEXTAREA_FONTSIZE = 16

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  const boardSize = ref(100)
  var blankAppendTrigger = ""

  const initialFen = ref(INITIAL_FEN)
  const forceFen = ref(INITIAL_FEN)
  var fenOfCurrentBoard = INITIAL_FEN

  const animateFen = ref([])
  const animateSequeneOfFens = ref([])
  const pieceSet = ref("regular")

  var fenAfterLastEngineMove = ""

  var playingWhite = 1
  var orientation = ref("white")

  var playerSans = []

  var scrollCounter = 0
  const scrollTrigger = ref(0)
  const explainScrollTrigger = ref(0)

  // move replay

  var moveHistorySans = []
  var moveHistoryFens = []
  var replayHistorySans = []
  var replayHistoryFens = []
  var fenBeforeReplay = ""
  var firstReplayMoveNumber = 1
  var startOfOpenGame = false

  var blindnessLevel = 0
  var isRevealed = false

  // OPTIONS
    
  const blindnessHeadings = ["Blindness Difficulty","Engine for Opponent Moves", "Engine Strength - Skill", "Color You Play", "Play Keyword Required ?"]
  const blindnessHeadingWords = ["blindness difficulty blind","engine for opponent moves","engine strength skill","color","play"]
  const blindnessVarnames = ["blindness", "engine", "skill", "color", "play_keyword"]

  const computerHeadings = ["Engine for Opponent Moves", "Engine Strength - Skill", "Color You Play", "Play Keyword Required ?"]
  const computerHeadingWords = ["engine for opponent moves","engine strength skill","color","play"]
  const computerVarnames = ["engine", "skill", "color", "play_keyword"]

  const useOptionHeadings = (playType == "computer") ? computerHeadings : blindnessHeadings
  const useOptionHeadingWords = (playType == "computer") ? computerHeadingWords : 
                                              blindnessHeadingWords
  const useOptionVarnames = (playType == "computer") ? computerVarnames : blindnessVarnames
  const useOptionValueStrings = []
  
  const blindnessChoiceStrings = ["Regular Pieces","One Color Pieces","Two Color Discs","One Color Disc","Empty Board"]
  const blindnessChoiceWords = ["regular pieces piece","one color pieces piece","two color discs disc","one color discs disc","empty board"]
  const blindnessChoiceValues = [ 1, 2, 3, 4, 5 ]
  var blindnessCurrentValue = 1
  var blindnessCurrentValueString = blindnessChoiceStrings[0]

  const colorChoiceStrings = ["Play as White", "Play as Black"]
  const colorChoiceWords = ["play as white", "play as black"]
  const colorChoiceValues = [ 1, 0 ]
  var colorCurrentValue = 1
  var colorCurrentValueString = colorChoiceStrings[0]

  const engineChoiceStrings = [ "1 : Komodo - Best To Start", "2 : Stockfish", "3 : Maia - Tuned to Lichess Ratings"]
  const engineChoiceWords = [ "one" , "two","three"]
  const engineChoiceValues = [ "komodo", "stockfish", "maia"]
  var engineCurrentValue = "komodo"
  var engineCurrentValueString = engineChoiceStrings[0]

  const playKeywordChoiceStrings =["Play Keyword Required","Piece Name Required - Including Pawn","No Play Keyword Required"]
  const playKeywordChoiceWords = ["play", "piece", "no"]
  const playKeywordChoiceValues = ["play", "piece", "no"]
  var playKeywordCurrentValue = "play"
  var playKeywordCurrentValueString = playKeywordChoiceStrings[0]

  const skillChoiceStrings = ["Skill 0 - Komo (150), SFish (1350), Maia (600)",
                              "Skill 1 - Komo (300), SFish (1430), Maia (700)",
                              "Skill 2 - Komo (450), SFish (1510), Maia (800)",
                              "Skill 3 - Komo (600), SFish (1590), Maia (900)",
                              "Skill 4 - Komo (750), SFish (1670), Maia (1000)",
                              "Skill 5 - Komo (900), SFish (1750), Maia (1100)",
                              "Skill 6 - Komo (1050), SFish (1830), Maia (1200)",
                              "Skill 7 - Komo (1200), SFish (1910), Maia (1300)",
                              "Skill 8 - Komo (1350), SFish (1990), Maia (1400)",
                              "Skill 9 - Komo (1500), SFish (2070), Maia (1500)",
                              "Skill 10 - Komo (1650), SFish (2150), Maia (1600)",
                              "Skill 11 - Komo (1800), SFish (2230), Maia (1700)",
                              "Skill 12 - Komo (1950), SFish (2310), Maia (1800)",
                              "Skill 13 - Komo (2100), SFish (2390), Maia (1900)",
                              "Skill 14 - Komo (2250), SFish (2470), Maia (2000)",
                              "Skill 15 - Komo (2400), SFish (2550), Maia (2100)",
                              "Skill 16 - Komo (2550), SFish (2630), Maia (2200)",
                              "Skill 17 - Komo (2700), SFish (2710), Maia (2300)",
                              "Skill 18 - Komo (2850), SFish (2790), Maia (2400)",
                              "Skill 19 - Komo (3000), SFish (2870), Maia (2500)",
                              "Skill 20 - Maximum, Maia (2600)"]
  const skillChoiceWords = ["skill zero","skill one","skill two","skill three","skill four","skill five","skill six","skill seven",
                              "skill eight","skill nine","skill ten","skill eleven","skill twelve","skill thirteen","skill fourteen",
                              "skill fifteen","skill sixteen","skill seventeen","skill eighteen","skill nineteen","skill twenty maximum"]
  const skillChoiceValues = [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]
  var skillCurrentValue = 0
  var skillCurrentValueString = skillChoiceStrings[0]

  var optionDisplay = []
  var optionDisplayWords = []
  var optionValue = []
  var optionCurrent = []
  var optionValueStrings = []

  const multipleOptionsArray = ref([])
  const singleOptionHeading = ref("")
  const singleOptionArray = ref([])
  var singleOptionDisplayWordsArray = []
  var useSingleOptionDisplayWords = ""
  var singleOptionServerField = ""
  const singleOptionSelected = ref("")

  // end OPTIONS

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "0px",
    width: "0px",
    height : "0px"
  })

  const styleTextArea = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "0px",
    width: "0px",
    height : "0px",
    "font-size" : TEXTAREA_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em"
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {

    updateWidthHeights()
    if (openGameId == 0)
       saySomething(true,translatePhrase("Shall we play a game?"))
    if (openGameId > 0)
      getOpenGame()
    else
      getUserInfo()
  }

  function updateStyleObjects(boundaries) {

    //console.log("boundaries",boundaries)
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleTextArea.value.height = boundaries['textArea'].height + "px"
    styleTextArea.value.width = boundaries['textArea'].width + "px"
    styleTextArea.value.top = boundaries['textArea'].top + "px"
    styleTextArea.value.left = boundaries['textArea'].left + "px"

    //console.log("**** boardsize ",boardSize.value,styleTextArea.value,styleBoard.value)
  }

  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)

    styleToolbar.value = buildStyleToolbar(availableSpace)
    styleTopline.value = buildStyleTopline(availableSpace)
    styleMiddle.value = buildStyleMiddle(availableSpace)

    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)
}

function calculatePortraitWidthHeights(availableSpace) {
  //console.log("available space ",availableSpace)

  const spaceForHeight = (showState.value == SHOW_PLAY) ?
                availableSpace.height - TEXTAREA_FONTSIZE * 7 * 1.2 :
                availableSpace.height /2
  // reserve space for the instruction lines
  const availableSpaceForBoard = { width : availableSpace.width ,
              height : spaceForHeight - availableSpace.topIndent - 56,
              isPortrait : true }

  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const textAreaPositionTop = boardPositionTop + boardHeight
  const textAreaPositionLeft = availableSpace.leftRightIndent
  const textAreaHeight = availableSpace.height - textAreaPositionTop
  const textAreaWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const result = { 
    board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft,
      top : boardPositionTop
    },
    textArea : {
      width : textAreaWidth,
      height : textAreaHeight,
      left : textAreaPositionLeft,
      top : textAreaPositionTop
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

function calculateLandscapeWidthHeights(availableSpace) {

  //console.log("available space ",availableSpace)

  const spaceForWidth = (showState.value == SHOW_PLAY) ? availableSpace.width - 150 :
                               availableSpace.width /2
  // reserve space for the instruction lines
  const availableSpaceForBoard = { width : spaceForWidth,
              height : availableSpace.height - availableSpace.topIndent - 56,
              isPortrait : false }
  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const textAreaPositionTop = 0
  const textAreaPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
  const textAreaHeight = availableSpace.height
  const textAreaWidth = availableSpace.width - textAreaPositionLeft

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft,
      top : boardPositionTop
    },
    textArea : {
      width : textAreaWidth,
      height : textAreaHeight,
      left : textAreaPositionLeft,
      top : textAreaPositionTop
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

 watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
      //console.log('*********** Explorer -- window size changes detected')
      windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] }
      //console.log("*** Explorer new window size",windowSizeRef.value)
      updateWidthHeights()
    })

 const keyState = {
    ArrowUp: false,
    ArrowDown: false,
    ArrowLeft: false,
    ArrowRight: false
  }

  const handleKeyDown = (event) => {
    if (event.key in keyState) {
      keyState[event.key] = true;
      // Optional: prevent page scrolling
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
      }
      console.log("keystate",keyState)
      if (keyState.ArrowLeft) {
        const describeString = processArrowKey("left",fileWords,fenOfCurrentBoard)
        saySomething(true,describeString)
      }
      else
      if (keyState.ArrowRight) {
        const describeString = processArrowKey("right",fileWords,fenOfCurrentBoard)
        saySomething(true,describeString)
      }
      else
      if (keyState.ArrowUp) {
        const describeString = processArrowKey("up",fileWords,fenOfCurrentBoard)
        saySomething(true,describeString)
      }
      else
      if (keyState.ArrowDown) {
        const describeString = processArrowKey("down",fileWords,fenOfCurrentBoard)
        saySomething(true,describeString)
      }
    }
  };

  const handleKeyUp = (event) => {
    if (event.key in keyState) {
      keyState[event.key] = false;
    }
  };

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
  })
  
  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
  })

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function saySomething(sayAsPlayer,moveString) {
    emit('string-to-speak',sayAsPlayer,moveString)
  }

  function toplineClick(whatClicked) {
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

   // START Explain Tour Routines

  function explainTourInitialize(screenPage,errorText) {
    // save which page called for an explanation
    explainForState = showState.value
    explainForStateWordList = wordlist.value

    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? screenPage : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)

    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "explain" :
        explainTourInitialize("options_multiple",null)
        break
      case "done" :
        // restore to who ever wanted an explanation
        showState.value = explainForState
        wordlist.value = explainForStateWordList
        emit("word-list",wordlist.value)
        wordlistTrigger.value++
        break
    }
  }

  // END Explain Tour Routines

  function repeatEngineMove() {
    if (engineMoveToRepeat == "")
      saySomething(false,translatePhrase("no engine move to repeat"))
    else
      saySomething(false,engineMoveToRepeat)
  }

 

  function multiOptionsClick(index) {
    const oneOption = multipleOptionsArray.value[index]
    singleOptionHeading.value = oneOption.heading
    singleOptionArray.value = []
    singleOptionDisplayWordsArray = []
    for (var i=0; i < oneOption.displayWords.length; i++) {
      singleOptionDisplayWordsArray.push(oneOption.displayWords[i])
      const oneDisplay = {
            index : i,
            name : translateEnglishWords(oneOption.display[i]),
            words : oneOption.displayWords[i],
            id : oneOption.values[i],
            radio : "" + oneOption.values[i]
      }
      singleOptionArray.value.push(oneDisplay)
    }
    singleOptionSelected.value = "" + oneOption.current
    singleOptionServerField = oneOption.varname
    useSingleOptionDisplayWords = buildWordListFromChoiceList(singleOptionDisplayWordsArray)

    singleOptionForEndGame = false
    wordlist.value = useSingleOptionDisplayWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    showState.value = SHOW_SINGLE_OPTION
  }

  function multiOptionsToolbarClick(selected) {
    toolbarTrigger.value++
    switch (selected) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "explain" :
        explainTourInitialize("options_multiple",null)
        break
      case "done" :
        getUserInfo()
        switchToPlay()
        break
    }
  }

  function singleOptionClick(index) {
    const databaseValue = singleOptionArray.value[index].radio
    singleOptionSelected.value = singleOptionArray.value[index].radio
    if (singleOptionForEndGame == false) {
      setOption(singleOptionServerField,databaseValue)
    }
  }

  function singleOptionToolbarClick(selected) {
    toolbarTrigger.value++
    switch (selected) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "explain" :
        explainTourInitialize("options_single",null)
        break
      case "done" :
        if (singleOptionForEndGame) {
          if (singleOptionSelected.value == "0") {
            //console.log("**** END GAME")
            setEndGame()
          }
          else {
            //console.log("**** NO END GAME")
          }
          switchToPlay()
        }
        else
          buildMultipleOptionsDisplay()
        break
    }
  }

  function runDelayAfterReceiveEngineMove() {

    let timerSleepAmount = 2500

    function oneInterval() {
      clearInterval(id)
      // toolbarElements.value[TOOLBAR_TAKEBACK].disabled = true
      // toolbarTrigger.value++

      applyEngineMove()
    }
    var id = setInterval(oneInterval, timerSleepAmount)
  }

  function getAvailableMoves(fen) {
    var fenPieces = fen.split(" ")
    fenPieces[5] = 1
    const fenFixed = fenPieces.join(" ")
    chess.load(fenFixed)
    const moves = chess.moves({ verbose: true })
    availableMoves.value = buildAvailableMoves(moves,false)
  }

  function boardMove(arrayIndex,from,to) {
    const oneMove = availableMoves.value[arrayIndex]
    forceFen.value = oneMove.fen
    fenOfCurrentBoard = oneMove.fen
    
    const speakString = buildVerbalSan(oneMove.chessJS,fileWords)
    //console.log("SPEAK STRING",speakString)
    saySomething(true,speakString)

    moveHistoryFens.push(oneMove.fen)
    moveHistorySans.push(oneMove.san)

    let replayString = buildReplayHistory()
    textareaMessage.value = "Your Move: " + oneMove.san + "<br>" + replayString

    canTakeBack = true
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = false
    toolbarTrigger.value++

    getEngineMove(oneMove.fen,oneMove.from,oneMove.to,oneMove.promotion,oneMove.san)
  }

  function switchToPlay() {
    showState.value = SHOW_PLAY
    wordlist.value = wordlistPlay
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    toolbarTrigger.value++
  }
 
  function toolbarClick(arrayIndex) {
    var useIndex = arrayIndex
    // handle the different locations of the Explain button for computer & blindfold
    if ((playType == "blindfold" || blindnessLevel >= 2) && useIndex == TOOLBAR_BLINDFOLD_EXPLAIN)
      useIndex = 10
    else
    if ((playType == "computer" || blindnessLevel < 2) && useIndex == TOOLBAR_COMPUTER_EXPLAIN)
      useIndex = 10

    switch (useIndex) {
      case TOOLBAR_START_GAME :
        if (gameState == GAME_ENDED)
          processWords("command new game")
        else
        if (gameState == NO_GAME)
          processWords("command start game")
        else
          processWords("command end game")
        break
      case TOOLBAR_TAKEBACK_OPTIONS :
        //console.log("process takeback")
        if (gameState == IN_GAME)
          processWords("command take back")
        else
          processWords("command options")
        break
      case TOOLBAR_REPLAY_REVIEW :
        //console.log("replay",gameState)
        if (gameState == IN_GAME)
          processWords("command replay")
        else
          processWords("command review")
        break
      case TOOLBAR_BLINDFOLD_REVEAL :
        processWords("command reveal");
        break
      case 10 :
        processWords("command explain")
        break;
    }
  }

  function verifyEndGame() {
    singleOptionHeading.value = translatePhrase("End Game?")
    singleOptionArray.value = []
    singleOptionDisplayWordsArray = []
    singleOptionDisplayWordsArray.push("yes")
    singleOptionDisplayWordsArray.push("no")
    var oneDisplay = { index : 0, name : translateEnglishWords("Yes"), id : 0,radio : "0" , words : "yes"}
    singleOptionArray.value.push(oneDisplay)
    oneDisplay = { index : 1, name : translateEnglishWords("No"), id : 1,radio : "1" , words : "no"}
    singleOptionArray.value.push(oneDisplay)
    singleOptionSelected.value = "1"
    singleOptionServerField = ""
    useSingleOptionDisplayWords = buildWordListFromChoiceList(singleOptionDisplayWordsArray)
    //console.log("****************** single option array",singleOptionArray.value)
    //console.log("****************** single option word list",useSingleOptionDisplayWords)

    singleOptionForEndGame = true
    wordlist.value = useSingleOptionDisplayWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    showState.value = SHOW_SINGLE_OPTION
  }
  
  function processWordsForPlay(text) {
    const returnArray = parseGameWordsSaid(text,gameState == IN_GAME,false,availableMoves,
                    playKeywordRequired)
    //console.log("raw speech text",text)
    //console.log("command found",returnArray)

    switch (returnArray[0]) {
      case "ambiguous" :
        textareaMessage.value = returnArray[1]
        saySomething(true,returnArray[2])
        break;
      case "play" :
        if (showState.value == SHOW_PLAY) {
          const arrayIndex = returnArray[1]
          boardMove(arrayIndex,0,0)
        }
        break
      case "describe" :
        const describeString = processDescribe(returnArray[1],returnArray[2],returnArray[3],returnArray[4], 
                returnArray[5],returnArray[6],fileWords,fenOfCurrentBoard)
        saySomething(true,describeString)
        break
      case "command" :
        switch (returnArray[1]) {
          case "start game" :
            if (gameState == NO_GAME)
              createNewGame()
            break
          case "end game" :
            if (gameState == IN_GAME)
              verifyEndGame()
            break
          case "new game" :
            //console.log("***** gamestate in New game",gameState)
            if (gameState == GAME_ENDED || gameState == NO_GAME) 
               createNewGame()
            break
          case "takeback" :
            //console.log("********** TAKEBACK ",gameState,toolbarElements.value[TOOLBAR_TAKEBACK].disabled)
            if (gameState == IN_GAME && canTakeBack) {
              //console.log("*************** DID TAKEBACK")
              // did take back. reset to before last player move

              canTakeBack = false
              toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = true
              toolbarTrigger.value++

              getMoveTakeBack()
            }
            break  
          case "reveal" :
          case "revert" :
            if (gameState == IN_GAME && blindnessLevel >= 2) {
              isRevealed = !isRevealed
              setPieces((isRevealed) ? 0 : blindnessLevel)
              //forceFen.value += " "
              toolbarElements.value[TOOLBAR_BLINDFOLD_REVEAL].label = (isRevealed) ? translateEnglishWords("Revert") : 
                                    translateEnglishWords("Reveal")
              toolbarTrigger.value++
            }
            break
          case "replay" :
            //console.log("replay command")
            if (gameState == IN_GAME) {
              buildReplayHistory()
              incrementAppendTrigger()
              forceFen.value = moveHistoryFens[0]
              animateSequeneOfFens.value = [moveHistoryFens[0]]
              for (var i=0; i < moveHistoryFens.length; i++)
                animateSequeneOfFens.value.push(moveHistoryFens[i])
            }
            break;
          case "review" :
            if (gameState != IN_GAME) {
              connectionStore.setReviewGameId(openGameId + ",/verbalplay")
              emit("where-next","/gamereview")
            }
            break
          case "repeat" :
            repeatEngineMove()
            break
          case "options" :
            if (showState.value == SHOW_PLAY)
              buildMultipleOptionsDisplay()
            break
          case "explain" :
            explainTourInitialize("verbalplay",null)
            break;
          case "mute" :
            microphoneIsMuted.value = true 
            emit("mic-is-muted",microphoneIsMuted.value)
            connectionStore.setMicrophoneIsMuted(true)
            break
          case "listen" :
            microphoneIsMuted.value = false 
            emit("mic-is-muted",microphoneIsMuted.value)
            connectionStore.setMicrophoneIsMuted(false)
            break
          case "done" :
            emit("where-next","/enginemenu")
            break;
        }
        break
    }
  }

  function processWords(text) {
    //console.log("text and state",showState.value)
    switch (showState.value) {
      case SHOW_EXPLAINTOUR :
        explainTourWordsSpoken.value = text
        explainTourWordsSpokenTrigger.value++
        break
      case SHOW_MULTIPLE_OPTIONS :
        multiOptionsWordsSpoken.value = text
        multiOptionsWordsSpokenTrigger.value++
        break
      case SHOW_SINGLE_OPTION :
        singleOptionWordsSpoken.value = text
        singleOptionWordsSpokenTrigger.value++
        break
      case SHOW_PLAY :
        processWordsForPlay(text)
        break
    }
  }
    
  function buildReplayHistory() {
    var isWhiteMove = true
    var replayString = ""
    var firstIndex = 0
    var moveCounter = 0
    replayHistorySans = []
    replayHistoryFens = []
    firstReplayMoveNumber = 1
    fenBeforeReplay = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
    
    if (moveHistorySans.length > 10) {
        if (moveHistorySans.length % 2 == 1) {
            firstIndex = moveHistorySans.length - 11
            firstReplayMoveNumber = firstIndex / 2 + 1
        }
        else {
            firstIndex = moveHistorySans.length - 10
            firstReplayMoveNumber = firstIndex / 2 + 1
        }
    }
    
    if (firstIndex > 0) {
        fenBeforeReplay = moveHistoryFens[firstIndex-1]
    }
    replayHistoryFens.push(fenBeforeReplay)
    moveCounter = firstReplayMoveNumber
    for (var i = firstIndex; i < moveHistorySans.length; i++) {
        if (isWhiteMove) {
            if (i != firstIndex) {
                replayString += " "
            }
            replayString += String(moveCounter) + "."
            moveCounter += 1
        }
        replayString += " " + moveHistorySans[i]
        replayHistoryFens.push(moveHistoryFens[i])
        replayHistorySans.push(moveHistorySans[i])
        
        isWhiteMove = !isWhiteMove
    }
    return (replayString)
  }
  
  function buildFirstMoveNote() {

    var firstMoveNote1 = ""
    var firstMoveNote2 = ""
    switch (playKeywordRequired) {
      case "no" :
        firstMoveNote1 = translatePhrase("No Play Keyword Required")
        firstMoveNote2 = translatePhrase("Just say e4 or echo 4")
        break;
      case "piece" :
        firstMoveNote1 = translatePhrase("Say piece name even if pawn")
        firstMoveNote2 = translatePhrase("Pawn e4 or Pawn echo 4")
        break;
      default :
        firstMoveNote1 = translatePhrase("Play Keyword Required")
        firstMoveNote2 = translatePhrase("Say 'play e4' or 'play echo 4'")
        break;
    }
    const firstMoveNote3 = translateEnglishWords("Piece Names : rook knight bishop queen king pawn")
    return ("<br>" + firstMoveNote1 + "<br>" + firstMoveNote2 + "<br>" + firstMoveNote3)
  }

  function applyEngineMove() {
    var engineVerbalSan = ""
    var engineChessJS = null
    var engineSan = ""
    var engineFen = engineMoveJson['engine_fen']

    if (engineFen == "")
      return

    // load the last player move, then get the possible engine moves against that
    //          find the move the engine made and use it's "move" record

    chess.load(fenOfCurrentBoard)
    const moves = chess.moves({ verbose: true })

    const engineFenParts = engineFen.split(" ")
    for (var i=0; i < moves.length; i++) {
      const fenParts = moves[i].after.split(" ")
      if (engineFenParts[0] == fenParts[0]) {
        engineChessJS = moves[i]
        break
      }
    }

    engineVerbalSan = buildVerbalSan(engineChessJS,fileWords)
    engineFen = moves[i].after
    engineSan = moves[i].san

    var firstMoveNote = ""
    if (startOfOpenGame || moveHistorySans.length == 0) {
       firstMoveNote = buildFirstMoveNote()
    }
    startOfOpenGame = false

    //console.log("in apply ",moveHistorySans.length, playKeywordRequired, firstMoveNote)
    animateFen.value = [forceFen.value , engineFen]
    
    saySomething(false,engineVerbalSan)
    engineMoveToRepeat = engineVerbalSan
    // fenAfterLastEngineMove = engineFen
    // fenOfCurrentBoard = engineFen

    moveHistorySans.push(engineSan)
    moveHistoryFens.push(engineFen)

    let replayString = buildReplayHistory()
    textareaMessage.value = (playingAsWhite == 1) ? translatePhrase("Black's Move") :
                                   translatePhrase("White's Move")
    textareaMessage.value += ": "
    textareaMessage.value += engineSan + "<br>" + replayString + "<br>" + firstMoveNote 
  
    // else {
    //   engineMoveToRepeat = ""
    //   let replayString = buildReplayHistory()

    //   textareaMessage.value = translatePhrase("Make Move") + "<br><br>" + replayString + "<br><br>" + firstMoveNote 
    // }

    fenOfCurrentBoard = engineFen
    getAvailableMoves(engineFen)

    toolbarElements.value[TOOLBAR_REPLAY_REVIEW].disabled = moveHistoryFens.length == 0
    toolbarTrigger.value++
  }

  function processOpenGameJsonResponse (json) {
    //console.log(" in open game callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    blindnessLevel = Math.floor(json["level"])
    setPieces(blindnessLevel) 

    playingAsWhite = json["white"]
    orientation.value = (playingAsWhite == 1) ? "white" : "black"

    forceFen.value = json['engine_fen']

    playKeywordRequired = json["play_keyword"]
    startOfOpenGame = true

    engineMoveToRepeat = ""

    moveHistorySans = json["sans"].split(",")
    moveHistoryFens = json["fens"].split(",")

    let replayString = buildReplayHistory()
    const firstMoveNote = buildFirstMoveNote()
    textareaMessage.value = translatePhrase("Make Move") + "<br>" + replayString +
              "<br>" + firstMoveNote

    fenAfterLastEngineMove = forceFen.value
    fenOfCurrentBoard = forceFen.value

    gameState = IN_GAME

    if (blindnessLevel >= 2) {
      toolbarElements.value = toolbarBlindElements
      toolbarElements.value[TOOLBAR_BLINDFOLD_REVEAL].disabled = (blindnessLevel < 2)
      toolbarElements.value[TOOLBAR_BLINDFOLD_REVEAL].label = translateEnglishWords("Reveal")
    }
    else
      toolbarElements.value = toolbarComputerElements

    toolbarElements.value[TOOLBAR_START_GAME].label = translateEnglishWords("End Game")
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].label = translateEnglishWords("Takeback")
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = true

    toolbarElements.value[TOOLBAR_REPLAY_REVIEW].disabled = moveHistorySans.length == 0
    toolbarElements.value[TOOLBAR_REPLAY_REVIEW].label = translateEnglishWords("Replay")

    isRevealed = false

    toolbarTrigger.value++

    saySomething(false,translatePhrase("Make Move"))

    getAvailableMoves(forceFen.value)
  }

  function getOpenGame() {
    var formData = {
      gameid : openGameId
    }
    useGetData("getweb6_load_open_game.php", formData, processOpenGameJsonResponse)
  }

  function processTakeBackJsonResponse (json) {
    //console.log(" in takeback callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }
    getOpenGame()
  }

  function getMoveTakeBack() {
    var formData = {
      gameid : openGameId,
    }
    useGetData("get_verbal_do_takeback.php", formData, processTakeBackJsonResponse)
  }

  function getArrayIndexFromValueMatch(choiceValues,current) {
    for (var i=0; i < choiceValues.length; i++)
      if (current == choiceValues[i])
        return (i)
    return (0)
  }

  function processUserInfoJsonResponse (json) {
    console.log("*************************** in userinfo callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    blindnessCurrentValue = json["blindness_number"]
    colorCurrentValue = json["color_number"]
    const engineCurrentNumber = parseInt(json["engine_number"])
    engineCurrentValue = (engineCurrentNumber == 2) ? "komodo" : "stockfish"
    switch (engineCurrentNumber) {
      case 2 :
        engineCurrentValue = "komodo"
        break
      case 3 :
        engineCurrentValue = "maia"
        break
      default :
        engineCurrentValue = "stockfish"
        break
    }
    skillCurrentValue = json["skill_number"]
    playKeywordCurrentValue = json["play_keyword"]

    const blindnessIndex = getArrayIndexFromValueMatch(blindnessChoiceValues,blindnessCurrentValue)
    const colorIndex = getArrayIndexFromValueMatch(colorChoiceValues,colorCurrentValue)
    const engineIndex = getArrayIndexFromValueMatch(engineChoiceValues,engineCurrentValue)
    const skillIndex = getArrayIndexFromValueMatch(skillChoiceValues,skillCurrentValue)
    const playKeywordIndex = getArrayIndexFromValueMatch(playKeywordChoiceValues,playKeywordCurrentValue)

    blindnessCurrentValueString = translatePhrase(blindnessChoiceStrings[blindnessIndex])
    colorCurrentValueString = translatePhrase(colorChoiceStrings[colorIndex])
    playKeywordCurrentValueString = translatePhrase(playKeywordChoiceStrings[playKeywordIndex])
    engineCurrentValueString = translatePhrase(engineChoiceStrings[engineIndex])
    skillCurrentValueString = translatePhrase(skillChoiceStrings[skillIndex])
    
    // little nicer text for display with board
    var engineString = translatePhrase("Using") + " "
    switch (engineCurrentValue) {
      case "komodo" :
        engineString += translatePhrase("Komodo (Best To Start) Engine")
        break
      case "maia" :
        engineString += translatePhrase("Maia - Tuned to Lichess Ratings")
        break
      default : 
        engineString += translatePhrase("Stockfish Engine")
        break
    }

    // little nicer text for display with board
    skillCurrentValueString = translatePhrase("Skill") + " "
    skillCurrentValueString += skillCurrentValue + " - "
    if (skillCurrentValue == 20)
      skillCurrentValueString += translatePhrase("Maximum")
    else {
      var ratingEquivalent = 0
      switch (engineCurrentValue) {
        case "komodo" :
          ratingEquivalent = 150 + 150 * skillCurrentValue 
          break
        case "maia" :
          ratingEquivalent = 600 + 100 * skillCurrentValue 
          break
        default :
          ratingEquivalent = 1350 + 80 * skillCurrentValue
          break
      }
      skillCurrentValueString += translatePhrase("Rating Equivalent") + " " + ratingEquivalent
    }
    
    if (playType == "computer")
      textareaMessage.value = colorCurrentValueString + "<br>" + engineString + "<br>" + skillCurrentValueString 
                    + "<br>" + playKeywordCurrentValueString
    else
      textareaMessage.value = blindnessCurrentValueString + "<br>" + colorCurrentValueString + "<br>" 
                    + engineString + "<br>" + skillCurrentValueString + "<br>" + playKeywordCurrentValueString
  }

  function getUserInfo() {
    var formData = {
      blah : "blah"
    }
    useGetData("get9_verbal_blind_user_info.php", formData,processUserInfoJsonResponse)
  }

  function setPieces(blindnessLevel) {
    switch (blindnessLevel) {
      case 0 :
      case 1 :
        pieceSet.value = "regular"
        break
      case 2 : 
        pieceSet.value = "one_color"
        break
      case 3 :
        pieceSet.value = "two_disks"
        break
      case 4 :
        pieceSet.value = "one_disk"
        break
      case 5 :
        pieceSet.value = "invisible"
        break
    }
  }

  function processCreateGameJsonResponse (json) {
    //console.log(" in create game callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    startOfOpenGame = false
    moveNumber = 0
    openGameId = json["gameid"]

    connectionStore.setPlayComputerGameId(openGameId)

    blindnessLevel = Math.floor(json["level"])
    setPieces(blindnessLevel) 
    //console.log("******* PIECESET",blindnessLevel,pieceSet.value)

    playingAsWhite = json["white"]
    playKeywordRequired = json["play_keyword"]

    //console.log("Play keyword in CREATE GAME",playKeywordRequired)

    orientation.value = (playingAsWhite == 1) ? "white" : "black"

    incrementAppendTrigger()
    forceFen.value = INITIAL_FEN + blankAppendTrigger
    fenAfterLastEngineMove = INITIAL_FEN
    availableMoves.value = []

    gameState = IN_GAME
    
    const firstMoveNote = buildFirstMoveNote()
    saySomething(false,translatePhrase("Make Move"))
    textareaMessage.value = translateEnglishWords("Make Move") + "<br>" + firstMoveNote

    canTakeBack = false

    if (blindnessLevel >= 2) {
      toolbarElements.value = toolbarBlindElements
      toolbarElements.value[TOOLBAR_BLINDFOLD_REVEAL].disabled = false
      toolbarElements.value[TOOLBAR_BLINDFOLD_REVEAL].label = translateEnglishWords("Reveal")
    }
    else {
      toolbarElements.value = toolbarComputerElements
      toolbarElements.value[TOOLBAR_REPLAY_REVIEW].disabled = true
    }

    toolbarElements.value[TOOLBAR_START_GAME].label = translateEnglishWords("End Game")
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].label = translateEnglishWords("Takeback")
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = true
    toolbarElements.value[TOOLBAR_REPLAY_REVIEW].label = translateEnglishWords("Replay")
    toolbarElements.value[TOOLBAR_REPLAY_REVIEW].disabled = true

    isRevealed = false

    toolbarTrigger.value++

    engineMoveJson = null
    engineMoveToRepeat = ""

    moveHistorySans = []
    moveHistoryFens = []

    if (playingAsWhite == 1) {
      getAvailableMoves(INITIAL_FEN)
    }
    else
      getEngineMove(INITIAL_FEN,0,0,"","")

    // saySomething(false,translatePhrase("Make Move"))
  }

  function createNewGame() {
    var formData = {
      blindfold : (playType == "blindfold") ? 1 : 0
    }
    useGetData("get19_verbal_create_game.php", formData,
                        processCreateGameJsonResponse)
  }

  function processEngineMoveJsonResponse(json) {
    //console.log(" in engine move callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    engineMoveJson = json
   
    runDelayAfterReceiveEngineMove()

  }

 function getEngineMove(fen,from_square,to_square,promotion,san) {

    engineMoveJson = null

    availableMoves.value = []

    var formData = {
      gameid : openGameId,
      fen : fen,
      from_square : from_square,
      to_square : to_square,
      promotion : promotion,
      san : san
    }
    //console.log("in get engine move ",formData)
    useGetData("getweb4_verbal_engine_move.php", formData,processEngineMoveJsonResponse)
  }

  function buildMultipleOptionsDisplay() {

    console.log("build engine choices", engineCurrentValueString,engineCurrentValue)
    if (playType == "computer") {
      optionDisplay = [ engineChoiceStrings,skillChoiceStrings,colorChoiceStrings,playKeywordChoiceStrings]
      optionDisplayWords = [ engineChoiceWords,skillChoiceWords,colorChoiceWords,playKeywordChoiceWords]
      optionValue = [ engineChoiceValues,skillChoiceValues,colorChoiceValues,playKeywordChoiceValues]
      optionCurrent = [engineCurrentValue,skillCurrentValue,colorCurrentValue,playKeywordCurrentValue]
      optionValueStrings = [engineCurrentValueString,skillCurrentValueString,colorCurrentValueString,playKeywordCurrentValueString]
    }
    else {
      optionDisplay = [ blindnessChoiceStrings,engineChoiceStrings,skillChoiceStrings,colorChoiceStrings,playKeywordChoiceStrings]
      optionDisplayWords = [ blindnessChoiceWords,engineChoiceWords,skillChoiceWords,colorChoiceWords,playKeywordChoiceWords]
      optionValue = [ blindnessChoiceValues,engineChoiceValues,skillChoiceValues,colorChoiceValues,playKeywordChoiceValues]
      optionCurrent = [blindnessCurrentValue,engineCurrentValue,skillCurrentValue,colorCurrentValue,playKeywordCurrentValue]
      optionValueStrings = [blindnessCurrentValueString,engineCurrentValueString,skillCurrentValueString,colorCurrentValueString,playKeywordCurrentValueString]
    }
    
    multipleOptionsArray.value = []
    for (var i = 0; i < optionDisplay.length; i++) {
      const oneOption = {
          index : i,
          heading : translateEnglishWords(useOptionHeadings[i]),
          headingWords : useOptionHeadingWords[i],
          varname : useOptionVarnames[i],
          display : optionDisplay[i],
          displayWords : optionDisplayWords[i],
          values : optionValue[i],
          current : optionCurrent[i],
          currentString : optionValueStrings[i]
      }
      multipleOptionsArray.value.push(oneOption)
    }
    //console.log("options",multipleOptionsArray.value)

    wordlist.value = buildWordListFromChoiceList(useOptionHeadingWords)
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    showState.value = SHOW_MULTIPLE_OPTIONS    
  }

  function processSetOptionJsonResponse (json) {
    //console.log(" in set option callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }
    getUserInfo()
  }

  function setOption(fieldName,value) {

    var sendValue = value
    if (fieldName == "engine") {
      switch (value) {
        case "komodo" :
          sendValue = 2
          break
        case "maia" :
          sendValue = 3
          break
        default :
          sendValue = 1
      }
    }

    var formData = {
      name : fieldName,
      value : sendValue
    }

    console.log("Set Option ",formData)
    useGetData("set1_blind_options_ios.php", formData,processSetOptionJsonResponse)
  }

  function processSetEndGameJsonResponse (json) {
    //console.log(" in set end game callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    toolbarElements.value[TOOLBAR_START_GAME].label = translateEnglishWords("New Game")
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].label = translateEnglishWords("Options")
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = false

    toolbarElements.value[TOOLBAR_REPLAY_REVIEW].label = translateEnglishWords("Review")
    toolbarElements.value[TOOLBAR_REPLAY_REVIEW].disabled = false

    if (playType == "blindfold") {
      toolbarElements.value[TOOLBAR_BLINDFOLD_REVEAL].disabled = true
    }

    toolbarTrigger.value++

    connectionStore.setPlayComputerGameId("0")
    gameState = NO_GAME
    availableMoves.value = []
  }

  function setEndGame() {

    availableMoves.value = []

    var formData = {
      gameid : openGameId
    }
    useGetData("set18_game_ended.php", formData, processSetEndGameJsonResponse)
  }

</script>

<style scoped>


</style>